(() => {
  const instances = new Map();
  const modes = new Set(['zodiac', 'chakra']);
  let floatDismissed = false;

  function bind(root) {
    if (instances.has(root) || root.dataset.finderReady === 'true') return;
    const dialog = root.querySelector('[data-finder-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const views = new Map(
      [...dialog.querySelectorAll('[data-finder-view]')].map(view => [view.dataset.finderView, view])
    );
    const resultLabel = dialog.querySelector('[data-finder-result-label]');
    const resultContent = dialog.querySelector('[data-finder-result-content]');
    if (!['start', 'zodiac', 'chakra', 'result'].every(name => views.has(name))) return;
    if (!resultLabel || !resultContent) return;

    const controller = new AbortController();
    const listen = (element, event, callback) => element.addEventListener(event, callback, { signal: controller.signal });
    let currentView = 'start';
    let currentMode = 'zodiac';
    let trigger = null;
    let savedOverflow = null;
    let revealFrame = null;
    let floatObserver = null;
    const floatExclusions = new Set();
    const floatingLinks = [...root.querySelectorAll('[data-finder-float]')];

    function updateFloat() {
      floatingLinks.forEach(float => { float.hidden = floatDismissed || floatExclusions.size > 0; });
    }

    function cancelReveal() {
      if (revealFrame !== null) cancelAnimationFrame(revealFrame);
      revealFrame = null;
      views.get('result').classList.remove('is-revealed');
    }

    function showView(name, focus = true) {
      if (!views.has(name)) return;
      cancelReveal();
      currentView = name;
      views.forEach((view, key) => { view.hidden = key !== name; });
      dialog.scrollTop = 0;
      if (focus && dialog.open) views.get(name).querySelector('[data-finder-heading]')?.focus({ preventScroll: true });
    }

    function lockScroll() {
      const style = document.documentElement.style;
      if (style.getPropertyValue('overflow') === 'hidden') return;
      savedOverflow = {
        value: style.getPropertyValue('overflow'),
        priority: style.getPropertyPriority('overflow')
      };
      style.setProperty('overflow', 'hidden');
    }

    function unlockScroll() {
      if (!savedOverflow) return;
      const style = document.documentElement.style;
      if (style.getPropertyValue('overflow') === 'hidden' && style.getPropertyPriority('overflow') === '') {
        if (savedOverflow.value) style.setProperty('overflow', savedOverflow.value, savedOverflow.priority);
        else style.removeProperty('overflow');
      }
      savedOverflow = null;
    }

    function finishClose(restoreFocus = true) {
      cancelReveal();
      unlockScroll();
      const previousTrigger = trigger;
      trigger = null;
      previousTrigger?.setAttribute('aria-expanded', 'false');
      showView('start', false);
      resultLabel.textContent = '';
      resultContent.replaceChildren();
      if (restoreFocus && previousTrigger?.isConnected) previousTrigger.focus({ preventScroll: true });
    }

    function open(name, opener) {
      if (dialog.open) return true;
      if ([...document.querySelectorAll('dialog[open]')].some(other => other !== dialog)) return true;
      if (trigger || savedOverflow) finishClose(false);
      currentMode = modes.has(name) ? name : 'zodiac';
      showView(modes.has(name) ? name : 'start', false);
      resultLabel.textContent = '';
      resultContent.replaceChildren();
      try {
        dialog.showModal();
      } catch {
        return false;
      }
      trigger = opener;
      trigger?.setAttribute('aria-expanded', 'true');
      lockScroll();
      views.get(currentView).querySelector('[data-finder-heading]')?.focus({ preventScroll: true });
      return true;
    }

    function close() {
      if (!dialog.open) return;
      dialog.close();
      finishClose();
    }

    function select(choice) {
      const mode = choice.dataset.mode;
      if (!modes.has(mode) || currentView !== mode) return;
      const stones = [...new Set((choice.dataset.stones || '').split(',').map(stone => stone.trim()).filter(Boolean))].slice(0, 3);
      const templates = [...root.querySelectorAll('template[data-finder-result]')];
      const matches = stones.map(stone => templates.find(candidate => candidate.dataset.finderResult === stone)).filter(template => template?.content);
      const content = document.createDocumentFragment();
      if (matches.length === 3) matches.forEach(template => content.append(template.content.cloneNode(true)));
      else {
        const fallback = root.querySelector('template[data-finder-empty]');
        if (!fallback?.content) return;
        content.append(fallback.content.cloneNode(true));
      }
      currentMode = mode;
      resultLabel.textContent = choice.dataset.label || '';
      resultContent.replaceChildren(content);
      showView('result');
      revealFrame = requestAnimationFrame(() => {
        revealFrame = null;
        if (dialog.open && currentView === 'result') views.get('result').classList.add('is-revealed');
      });
    }

    listen(root, 'click', event => {
      if (!(event.target instanceof Element)) return;
      const target = event.target.closest('[data-finder-open], [data-finder-close], [data-finder-back], [data-finder-restart], [data-finder-mode], [data-finder-choice], [data-finder-dismiss]');
      if (!target || target.closest('[data-mh-finder]') !== root) return;
      if (target.matches('[data-finder-open]')) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (open(target.dataset.finderOpen, target)) event.preventDefault();
        return;
      }
      if (target.matches('[data-finder-dismiss]')) {
        event.preventDefault();
        floatDismissed = true;
        instances.forEach((instance, finderRoot) => finderRoot.querySelectorAll('[data-finder-float]').forEach(float => { float.hidden = true; }));
        return;
      }
      if (!dialog.open || !dialog.contains(target)) return;
      event.preventDefault();
      if (target.matches('[data-finder-close]')) close();
      else if (target.matches('[data-finder-restart]')) showView('start');
      else if (target.matches('[data-finder-back]')) showView(currentView === 'result' ? currentMode : 'start');
      else if (target.matches('[data-finder-mode]') && modes.has(target.dataset.finderMode)) {
        currentMode = target.dataset.finderMode;
        showView(currentMode);
      } else if (target.matches('[data-finder-choice]')) select(target);
    });

    listen(dialog, 'click', event => {
      if (event.target !== dialog || !dialog.open) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
    });
    listen(dialog, 'close', () => { if (!dialog.open) finishClose(); });
    listen(dialog, 'cancel', () => { cancelReveal(); });

    const destroy = () => {
      if (dialog.open) dialog.close();
      finishClose(false);
      controller.abort();
      floatObserver?.disconnect();
      floatExclusions.clear();
      root.removeAttribute('data-finder-ready');
      instances.delete(root);
    };
    instances.set(root, { destroy });
    updateFloat();
    if (floatingLinks.length && 'IntersectionObserver' in window) {
      floatObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) floatExclusions.add(entry.target);
          else floatExclusions.delete(entry.target);
        });
        updateFloat();
      });
      [root.querySelector('.mh-finder-invitation'), document.querySelector('.mh-mini-footer'), document.querySelector('.mh-payment-logos')]
        .filter(Boolean).forEach(element => floatObserver.observe(element));
    }
    showView('start', false);
    root.dataset.finderReady = 'true';
  }

  function scan(scope) {
    if (scope instanceof Element && scope.matches('[data-mh-finder]')) bind(scope);
    scope.querySelectorAll?.('[data-mh-finder]').forEach(bind);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (root === event.target || event.target.contains(root)) instance.destroy();
    });
  });
})();
